import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { PATHS } from 'routes/routes.constants';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';
import { useRouter } from '@polyai/common/hooks/useRouter';
import {
  AgentStudioIcon,
  CallEndIcon,
  PhoneIcon,
  VisibilityIcon,
} from '@polyai/ui/components/atoms/Icons';

import Helmet from 'components/molecules/Helmet';
import useCall from 'components/organisms/CallPanel/hooks/useCall';
import useBannerOffset from 'hooks/useBannerOffset';
import { generateNextPath } from 'lib/routeHelper';
import { LeftPanel } from 'screens/AgentWizard/components/LeftPanel';
import ProgressSliderWrapper from 'screens/AgentWizard/components/ProgressSliderWrapper';
import { Wrapper } from 'screens/AgentWizard/components/Wrapper';
import {
  ASSETS,
  type VoiceVariant,
  WIZARD_STEPS,
} from 'screens/AgentWizard/constants';
import VoiceOrb, { type VoiceOrbHandle } from '../Voice/components/VoiceOrb';
import { useAgentSetupFlow } from '../Voice/hooks/useAgentSetup';
import { ScenarioCard } from './components/ScenarioCard';
import {
  AgentStudioButton,
  AgentStudioSection,
  AgentStudioText,
  AnimationContainer,
  ButtonsContainer,
  CallButton,
  CloseButton,
  ContentContainer,
  ExampleQuestions,
  Logo,
  MainContent,
  MainTitle,
  MobileLogo,
  QuestionText,
  RightPanel,
  ScenariosContent,
  ScenariosDescription,
  ScenariosHeader,
  ScenariosPanel,
  ScenariosTitle,
  ScenariosTitleRow,
  ScenariosToggle,
  Subtitle,
  TitleSection,
} from './Scenario.styled';
import { SCENARIO_COPY } from './Scenario.uiCopy';

const AgentWizardScenario = () => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();

  const { pushUrl } = useRouter();
  const queryClient = useQueryClient();
  const bannerOffset = useBannerOffset();

  const {
    isLoading: isAgentSetupLoading,
    error: agentSetupError,
    agentSetupResult,
    hasInitiallyChecked,
    navigateToVoice,
    faqs,
    scenarios,
  } = useAgentSetupFlow();

  useEffect(() => {
    // Only navigate away if we've checked the backend and there's an error or no result
    if (
      hasInitiallyChecked &&
      (agentSetupError || (!isAgentSetupLoading && !agentSetupResult))
    ) {
      void navigateToVoice();
    }
  }, [
    agentSetupError,
    agentSetupResult,
    isAgentSetupLoading,
    hasInitiallyChecked,
    navigateToVoice,
  ]);

  // Random orb variant since they no longer select voices with orbs
  const [voice] = useState<VoiceVariant>(() =>
    Math.random() < 0.5 ? 'marin' : 'cedar',
  );

  // Invalidate cache to refetch with latest data on mount
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: [accountId, projectId],
    });
  }, [accountId, projectId, queryClient]);

  // Call functionality
  const {
    isReadyToStream,
    isOpeningStream,
    isStreaming,
    startCall,
    endCall,
    isAgentSpeaking,
  } = useCall({
    isEnabled: true,
  });

  const [showScenarios, setShowScenarios] = useState(true);
  const [showAgentStudio, setShowAgentStudio] = useState(false);
  const [hasCompletedCall, setHasCompletedCall] = useState(false);

  const orbRef = useRef<VoiceOrbHandle | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  const prevIsStreamingRef = useRef(isStreaming);

  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    const isNotStreamingNow = !isStreaming;

    if (wasStreaming && isNotStreamingNow && !hasCompletedCall) {
      setHasCompletedCall(true);
    }

    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, hasCompletedCall]);

  useEffect(() => {
    if (hasCompletedCall) {
      setShowAgentStudio(true);
    }
  }, [hasCompletedCall]);

  const handleCallToggle = () => {
    if (isStreaming) {
      endCall();
    } else {
      void startCall();
    }
  };

  const handleAgentStudioClick = () => {
    const agentUrl = generateNextPath(PATHS.AGENT, { accountId, projectId });
    pushUrl(agentUrl);
  };

  return (
    <>
      <Helmet title="Agent Wizard - Scenario" />
      <Wrapper>
        <LeftPanel bgUrl={ASSETS.LOGIN_BG}>
          <Logo aria-label="PolyAI" />
        </LeftPanel>
        <RightPanel>
          <MobileLogo aria-label="PolyAI" />
          <ProgressSliderWrapper step={WIZARD_STEPS.AGENT} />
          <ContentContainer ref={contentContainerRef}>
            <MainContent>
              <TitleSection>
                <MainTitle>{SCENARIO_COPY.title}</MainTitle>
                <Subtitle>{SCENARIO_COPY.subtitle}</Subtitle>

                <ExampleQuestions data-test-id="agent-wizard-example-questions">
                  {faqs.length > 0
                    ? faqs.map((faq, index: number) => (
                        <li
                          // biome-ignore lint/suspicious/noArrayIndexKey: existing
                          key={index}
                          data-test-id={`agent-wizard-faq-${index}`}
                        >
                          <QuestionText>{faq.question}</QuestionText>
                        </li>
                      ))
                    : SCENARIO_COPY.exampleQuestions.map((question, index) => (
                        <li
                          // biome-ignore lint/suspicious/noArrayIndexKey: existing
                          key={index}
                          data-test-id={`agent-wizard-example-question-${index}`}
                        >
                          <QuestionText>{question}</QuestionText>
                        </li>
                      ))}
                </ExampleQuestions>
              </TitleSection>

              <AnimationContainer data-test-id="agent-wizard-voice-orb-container">
                <VoiceOrb
                  ref={orbRef}
                  isSpeaking={isAgentSpeaking}
                  variant={voice}
                />
              </AnimationContainer>

              <ButtonsContainer>
                <ScenariosToggle
                  aria-controls="agent-scenarios-panel"
                  aria-expanded={showScenarios}
                  data-test-id="agent-wizard-scenarios-toggle"
                  onClick={() => setShowScenarios(!showScenarios)}
                >
                  <VisibilityIcon size="sm" />
                  {SCENARIO_COPY.scenarios.toggleShow}
                </ScenariosToggle>
                <CallButton
                  $isActive={isStreaming}
                  data-test-id="agent-wizard-call-button"
                  disabled={!isReadyToStream || isOpeningStream}
                  onClick={handleCallToggle}
                >
                  {isStreaming ? <CallEndIcon /> : <PhoneIcon />}
                  {isOpeningStream
                    ? 'Connecting...'
                    : isStreaming
                      ? SCENARIO_COPY.call.stop
                      : SCENARIO_COPY.call.start}
                </CallButton>
              </ButtonsContainer>
            </MainContent>
          </ContentContainer>

          <AgentStudioSection
            $visible={showAgentStudio}
            data-test-id="agent-wizard-studio-section"
          >
            <AgentStudioText>{SCENARIO_COPY.agentStudio.text}</AgentStudioText>
            <AgentStudioButton
              $visible={showAgentStudio}
              data-test-id="agent-wizard-studio-button"
              onClick={handleAgentStudioClick}
            >
              <AgentStudioIcon size="sm" />
              {SCENARIO_COPY.agentStudio.button}
            </AgentStudioButton>
          </AgentStudioSection>
        </RightPanel>

        <ScenariosPanel
          $bannerOffset={bannerOffset}
          $open={showScenarios}
          data-test-id="agent-wizard-scenarios-panel"
          id="agent-scenarios-panel"
        >
          <ScenariosHeader>
            <ScenariosTitleRow>
              <ScenariosTitle>{SCENARIO_COPY.scenarios.title}</ScenariosTitle>
              <CloseButton
                data-test-id="agent-wizard-scenarios-close"
                onClick={() => setShowScenarios(false)}
              >
                <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
              </CloseButton>
            </ScenariosTitleRow>
            <ScenariosDescription>
              {SCENARIO_COPY.scenarios.description}
            </ScenariosDescription>
          </ScenariosHeader>

          <ScenariosContent data-test-id="agent-wizard-scenarios-content">
            {scenarios.length > 0
              ? scenarios.map((scenario, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: existing
                  <ScenarioCard key={index} index={index} scenario={scenario} />
                ))
              : SCENARIO_COPY.scenarios.items.map((scenario, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: existing
                  <ScenarioCard key={index} index={index} scenario={scenario} />
                ))}
          </ScenariosContent>
        </ScenariosPanel>
      </Wrapper>
    </>
  );
};

export default AgentWizardScenario;
